import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Paper,
  Snackbar,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { spacing } from "@mui/system";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import DocumentViewer from "../../../components/viewers/DocumentViewer";
import useAuth from "../../../hooks/useAuth";
import {
  useGetAIOutputQuery,
  useGetContractQuery,
  useGetContractsQuery,
  useGetDocumentsQuery,
  useGetPromptsQuery,
  useGetSettingsQuery,
  useLazyGetExportContractQuery,
} from "../../../redux/slices/indexApiSlice";
import {
  AIReadingStatus,
  ContractStatus,
  IContract,
} from "../../../types/contract";
import { DialogMode } from "../../../types/dialogmode";
import { IDocument } from "../../../types/document";
import { ISignature, ISignee } from "../../../types/signature";
import { IGraphUser } from "../../../types/user";
import AIContractDetailSidebar from "./components/AIContractDetailSidebar";
import { INSIGHTS_ANALYSIS_TAB_ITEMS } from "./components/AIContractDetails/types";
import ContractHeader from "./components/ContractHeader";

import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { ILocationInText, IPrompt } from "../../../types/prompt";
import { ContractExtractionResult, Paragraph } from "../../../types/aiOutput";
import AnalyzingContractsLoader from "../../../components/AnalyzingContractsLoader";
import useContract from "../../../hooks/useContract";
import BreadCrumbsBar from "../../../components/breadcrumbsBar/BreadCrumbsBar";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AIContractDetail = ({
  selectedContractId,
  selectedPrompt,
  hideHeader,
}: {
  selectedContractId?: string;
  selectedPrompt?: IPrompt;
  hideHeader?: boolean;
}) => {
  const { t } = useTranslation();
  const { user, method } = useAuth();
  const { clientId, contractId, documentId, pageMode, status, folder } =
    useParams();
  const navigate = useNavigate();

  const users = useSelector((state: RootState) => state.users.users);

  const {
    data: contracts,
    isLoading: contractsLoading,
    isSuccess: contractsLoaded,
  } = useGetContractsQuery(status as ContractStatus);

  const [sideBarTab, setSideBarTab] = useState(
    INSIGHTS_ANALYSIS_TAB_ITEMS.BASIC_DETAILS
  );

  const switchToDocument = (doc: IDocument) => {
    if (doc.type === "contract") {
      // select the main document
      // reset the selected prompt
      setPrompt(undefined);
      setParagraph(undefined);
      setLocationInText([]);
      setAiOutput(undefined);

      setDocument(doc);

      navigate(`/contract/${doc.contractId}`, {
        replace: true,
      });
    } else if (doc.type === "attachment") {
      // select the attachment
      // setDocument(doc);
      navigate(`/contract/${doc.contractId}/document/${doc.id}`, {
        replace: true,
      });
    }
  };

  const {
    data: contract,
    isLoading: contractLoading,
    isSuccess: contractLoaded,
    isFetching: contractFetching,
    isError,
    refetch: getContract,
  } = useGetContractQuery((contractId || selectedContractId) ?? skipToken);

  // get the documents for the contract
  const {
    data: documents = [],
    isLoading: documentsLoading,
    isFetching: documentsFetching,
  } = useGetDocumentsQuery((contractId || selectedContractId) ?? skipToken);

  const { data: settings } = useGetSettingsQuery();

  const [exportContract, { data: exportContractData }] =
    useLazyGetExportContractQuery();

  // const [
  //   getAIOutput,
  //   {
  //     data: aiOutputs = [],
  //     isLoading: aiOutputsLoading,
  //     isFetching: aiOutputsFetching,
  //   },
  // ] = useLazyGetAIOutputQuery();
  const {
    data: aiOutputs = [],
    isLoading: aiOutputsLoading,
    isFetching: aiOutputsFetching,
  } = useGetAIOutputQuery((contractId || selectedContractId) ?? skipToken);

  const { data: prompts, isLoading: promptsLoading } = useGetPromptsQuery();

  const [mode, setMode] = useState<DialogMode>(DialogMode.ViewOnly);

  const [document, setDocument] = useState<IDocument>();
  const [currentIndex, setCurrentIndex] = useState(0);

  const { contractProgresses, isAnalyzing, analyzingNumber, analyzingSuccess } =
    useContract();

  useEffect(() => {
    if (contractProgresses) {
      if (contractProgresses.find((c) => c.contractId === contractId)) {
        // the contract is in the progresses
        getContract();
      }
    }
  }, [contractProgresses]);

  const [analysisStatus, setAnalysisStatus] = useState<AIReadingStatus>(
    AIReadingStatus.NONE
  );
  // const [initialContract, setInitialContract] = useState<IContract>();
  const [signer, setSigner] = useState<ISignee>();
  const [signees, setSignees] = useState<Array<ISignee>>([]);
  const [signatures, setSignatures] = useState<Array<ISignature>>([]);
  const [reloadSignatures, setReloadSignatures] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [searchForText, setSearchForText] = useState<string>("");
  const [prompt, setPrompt] = useState<IPrompt>();

  const [locationInText, setLocationInText] = useState<ILocationInText[]>([]);
  const [promptIdToExpand, setPromptIdToExpand] = useState<string>();
  const [aiOutput, setAiOutput] = useState<ContractExtractionResult>();
  const [paragraph, setParagraph] = useState<Paragraph>();

  // For some reason states (contract, signatures) is not updated
  // in saving functions (handleSaveAsDraft and handleSaveAndPublish).
  // So I use useRef to solve this problem.
  const updatedContractDataRef = useRef<{
    contract: IContract | undefined;
    signatures: ISignature[];
  }>({ contract: undefined, signatures: [] });

  updatedContractDataRef.current = {
    contract: contract ? { ...contract } : undefined,
    signatures: [...signatures],
  };

  useEffect(() => {
    if (documents.length > 0) {
      if (documentId) {
        // if a documentId is provided, select the document with that id
        const doc = documents.find((d) => d.id === documentId);
        if (doc) {
          setDocument(doc);
        }
      } else {
        // select the main contract
        const mainDoc = documents.find((d) => d.type === "contract");
        if (mainDoc) {
          setDocument(mainDoc);
        } else {
          setDocument(documents[0]);
        }
      }
    }
  }, [documents, documentId]);

  useEffect(() => {
    if (contract) {
      const newSignees = contract.signatures?.map((sig: ISignature) => {
        const signee: ISignee = {
          displayName: sig.displayName,
          email: sig.email,
          hasSigned: !!sig.signedDate,
          signatureId: sig.id,
          isApproved: sig.isApproved,
        };
        return signee;
      });

      if (newSignees) {
        setSignees(newSignees);
      }

      const newSignatures = contract.signatures?.map((sig: ISignature) => ({
        ...sig,
        draggable: !sig.isSigned,
      }));

      if (newSignatures) {
        setSignatures(newSignatures);
      }

      if (contract.analyzeStatus && contract.analyzeStatus !== analysisStatus) {
        setAnalysisStatus(contract.analyzeStatus);
      }
    }
  }, [contract, reloadSignatures, contractId, selectedContractId]);

  useEffect(() => {
    if (isError) {
      navigate("/404");
    }
  }, [isError]);

  // useEffect(() => {
  //   if (contract) {
  //     getAIOutput(contract);
  //   }
  // }, [contract]);

  useEffect(() => {
    if (pageMode) {
      if (pageMode === "edit") {
        setMode(DialogMode.Edit);
      }
    }
  }, [pageMode]);

  useEffect(() => {
    if (
      contract?.adminIds?.some((id) => id === user.id) ||
      contract?.managerIds?.some((id) => id === user.id) ||
      contract?.ownerIds?.some((id) => id === user.id) ||
      settings?.superAdmins?.some((id: string) => id === user.id)
    ) {
      setMode(DialogMode.Edit);
    } else if (contract?.readerIds?.some((id) => id === user.id)) {
      setMode(DialogMode.ViewOnly);
    } else {
      setMode(DialogMode.ViewOnly);
    }
  }, [
    contract?.adminIds,
    contract?.managerIds,
    contract?.ownerIds,
    contract?.readerIds,
    settings?.superAdmins,
  ]);

  useEffect(() => {
    // hide analysis alert (success or failed)
    if (
      mode === DialogMode.ViewOnly &&
      (analysisStatus === AIReadingStatus.COMPLETED ||
        analysisStatus === AIReadingStatus.ERROR)
    ) {
      setAnalysisStatus(AIReadingStatus.NONE);
    }
  }, [mode]);

  useEffect(() => {
    if (selectedPrompt) {
      setPrompt(selectedPrompt);
    }
  }, [selectedPrompt]);

  useEffect(() => {
    if (promptIdToExpand === "") {
      setPrompt(undefined);
    } else if (promptIdToExpand && !prompt) {
      // a prompt is expanded, but the prompt in this component is empty
      // select the prompt
      const newPrompt = prompts?.find((p) => p.id === promptIdToExpand);
      if (newPrompt) {
        setPrompt(newPrompt);
      }
    } else if (promptIdToExpand) {
      const newPrompt = prompts?.find((p) => p.id === promptIdToExpand);
      if (newPrompt) {
        setPrompt(newPrompt);
      }
    }

    if (promptIdToExpand) {
      // switch to the Fields tab
      setSideBarTab(INSIGHTS_ANALYSIS_TAB_ITEMS.FIELDS_FOUND);
    }
  }, [promptIdToExpand]);

  useEffect(() => {
    if (
      !contractId &&
      !selectedContractId &&
      !contractLoading &&
      contracts &&
      contracts?.length > 0
    ) {
      // if no id is provided, redirect to the first contract
      navigate(`/contracts/${contracts[0].id}`);
    }

    if (
      contractId &&
      !selectedContractId &&
      !contractLoading &&
      contracts &&
      contracts?.length > 0
    ) {
      const currentIndex = contracts?.findIndex(
        (c: IContract) => c.id === contractId
      );

      setCurrentIndex(currentIndex);
    }
  }, [contractId, selectedContractId, contractLoading, contracts]);

  useEffect(() => {
    if (contract && contract.signatures) {
      const userHaveSignature = contract.signatures.find(
        (sig: ISignature) => sig.email === user.email
      );
      console.log("userHaveSignature", userHaveSignature);
      if (userHaveSignature) {
        setSigner({
          displayName: user.displayName,
          email: user.email,
          hasSigned: !!userHaveSignature.signedDate,
          signatureId: userHaveSignature.id,
        });
      }
    }
  }, [contract, user]);

  const prev = () => {
    if (contracts && contracts.length > 0) {
      const prevContractId = contracts[currentIndex - 1]?.id || null;

      if (prevContractId) {
        if (clientId) {
          navigate(
            `/contracts-under-management/${clientId}/contract/${prevContractId}/${mode}`
          );
        } else {
          navigate(`/contract/${prevContractId}/${mode}`);
        }
      }
    }
  };

  const next = async () => {
    if (contracts && contracts.length > 0) {
      // get the id of the next contract

      const nextContractId = contracts[currentIndex + 1]?.id || null;

      if (nextContractId) {
        if (clientId) {
          navigate(
            `/contracts-under-management/${clientId}/contract/${nextContractId}/${mode}`
          );
        } else {
          navigate(`/contract/${nextContractId}/${mode}`);
        }
      }
    }
  };

  const handleAnalysis = () => {
    if (document) {
      setShowSnackbar(true);
    }
  };

  const storeUsers = (): IGraphUser[] => {
    let userStore: IGraphUser[] = [];

    // add the add users to a new contract, so send notification and mail of expired contact.
    if (contract && method !== "Google" && users && users.length) {
      // If method is Google then we stored this user by login step
      let userIds: string[] =
        (contract && contract.ownerIds) || (contract && contract.managerIds)
          ? [...contract?.ownerIds, ...contract?.managerIds]
          : [];

      const userIdsUnique = userIds?.filter((value, index, self) => {
        return self.indexOf(value) === index;
      });
      userStore = users
        .filter((u: IGraphUser) =>
          userIdsUnique.some((id: string) => id == u.id)
        )
        .map((item: IGraphUser) => {
          item = { ...item, mail: item.mail || item.userPrincipalName };
          return item;
        });
    }

    return userStore;
  };

  const handleClose = () => {
    setShowSnackbar(false);
  };

  const isUpSm = window.innerWidth > 600;

  return (
    <React.Fragment>
      {hideHeader ? null : (
        <>
          <Helmet title={contract?.name} />

          <Grid justifyContent="space-between" container wrap="nowrap">
            <BreadCrumbsBar
              aria-label="Breadcrumb"
              sx={{
                marginBottom: "20px",
              }}
            >
              <Link component={NavLink} to="/contracts" color="inherit">
                <Typography variant="h5" gutterBottom display="inline">
                  {t("Contracts")}
                </Typography>
              </Link>

              {folder && (
                <Link
                  component={NavLink}
                  to={`/contracts/folders/${folder}`}
                  color="inherit"
                >
                  <Typography variant="h5" gutterBottom display="inline">
                    {t(folder)}
                  </Typography>
                </Link>
              )}

              {status && (
                <Link
                  component={NavLink}
                  to={`/contracts/${status}`}
                  color="inherit"
                >
                  <Typography variant="h5" gutterBottom display="inline">
                    {t(status)}
                  </Typography>
                </Link>
              )}

              <Typography
                variant="h5"
                gutterBottom
                display="inline"
                color="primary"
              >
                {contract?.name}
              </Typography>
            </BreadCrumbsBar>
          </Grid>

          {/* {contract?.dates && (
            <Box px={6} mb={6} maxWidth="md">
               <ContractStatusStepper contract={contract} /> 
              <ContractTimeline
                contract={contract}
                documents={documents}
                aiOutputs={aiOutputs}
              />
            </Box>
          )} */}
        </>
      )}

      <Paper
        sx={{
          marginLeft: "-20px",
          marginRight: "-8px",
          marginBottom: "-20px",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          backgroundColor: "transparent",
        }}
        variant="elevation"
      >
        <Grid
          container
          direction="column"
          gap={3}
          sx={{
            flexGrow: 1,
          }}
        >
          {/* {contractLoading && (
          <Box textAlign="center">
            <CircularProgress />
          </Box>
        )} */}

          {contractId && !contractLoading && !contract?.id && (
            <Alert severity="error" sx={{ mb: 6 }}>
              <AlertTitle>{t("An error occurred!")}</AlertTitle>
              {t("Contract not found")}
            </Alert>
          )}

          {/* only show completed if modified was within the last 5 minutes */}
          {/* {analysisStatus === AIReadingStatus.COMPLETED &&
          isAfter(
            new Date(epochToDate(contract?.modified)),
            new Date(epochToDate(Date.now() - 300000))
          ) && (
            <Alert severity="success" sx={{ mb: 6 }}>
              <AlertTitle>{t("Success")}</AlertTitle>
              {t("Reading success")}
            </Alert>
          )}
         */}

          {analysisStatus === AIReadingStatus.STOPPED && (
            <Alert severity="info" sx={{ mb: 6 }}>
              <AlertTitle>{t("Stopped")}</AlertTitle>
              {t("Analysis stopped")}
            </Alert>
          )}
          {analysisStatus === AIReadingStatus.ERROR && (
            <Alert severity="error" sx={{ mb: 6 }}>
              <AlertTitle>{t("An error occurred!")}</AlertTitle>
              {t("Reading failed")}
            </Alert>
          )}

          <AnalyzingContractsLoader contract={contract} />

          <Grid
            container
            justifyContent="flex-start"
            alignItems="flex-start"
            direction="column"
            spacing={3}
            flexGrow={1}
          >
            {documents?.filter((d) => d.type === "attachment").length > 0 && (
              <Grid
                container
                size={12}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                paddingX={2}
              >
                <Grid container gap={2}>
                  <Chip
                    // label={contract?.name}
                    label={t("Main document")}
                    variant={
                      document?.type === "contract" ? "filled" : "outlined"
                    }
                    color={"primary"}
                    onClick={(e) => {
                      const mainDoc = documents.find(
                        (d) => d.type === "contract"
                      );
                      if (mainDoc) {
                        switchToDocument(mainDoc);
                      }
                    }}
                  />
                  {documents
                    ?.filter((d) => d.type === "attachment")
                    ?.map((att, i) => (
                      <Chip
                        key={`chip-${att.id}`}
                        label={`${t("Attachment")} ${(i + 1).toString()}: ${
                          att.name
                        }`}
                        variant={
                          att.id === document?.id ? "filled" : "outlined"
                        }
                        color={att.id === document?.id ? "primary" : "default"}
                        onClick={(e) => switchToDocument(att)}
                      />
                    ))}
                </Grid>

                {contract?.id && (
                  <Grid>
                    <ContractHeader contract={contract} documents={documents} />
                  </Grid>
                )}
              </Grid>
            )}

            <Grid container direction="row" size={12} flexGrow={1}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined">
                  <DocumentViewer
                    viewMode="withMenu"
                    contract={contract}
                    document={document}
                    aiOutputs={aiOutputs.filter(
                      (a) => a.documentId === document?.id
                    )} // only pass the AI output for the selected document so it will only select those highlight fields
                    signer={signer}
                    signable={false}
                    signatures={signatures}
                    setSignatures={setSignatures}
                    paging={false} // true
                    searchForText={searchForText}
                    prompt={prompt}
                    paragraph={paragraph}
                    expandPromptInSidebar={(promptId: string) => {
                      setPromptIdToExpand(promptId);
                    }}
                    aiOutput={aiOutput}
                    disableAppBar
                    // disableScroller
                  />
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                {contract && document && (
                  <AIContractDetailSidebar
                    mode={mode}
                    contract={contract}
                    documents={documents}
                    document={document}
                    aiOutputs={aiOutputs}
                    signer={signer}
                    signees={signees}
                    onAnalysis={handleAnalysis}
                    setSignees={setSignees}
                    setSignatures={setSignatures}
                    signatures={signatures}
                    resetSignatures={setReloadSignatures}
                    analysisStatus={analysisStatus}
                    setSearchForText={setSearchForText}
                    promptIdToExpand={promptIdToExpand}
                    handlePromptClick={(p?: IPrompt) => {
                      setPrompt(p);
                    }}
                    setAiOutput={setAiOutput}
                    setSelectedParagraph={setParagraph}
                    sideBarTab={sideBarTab}
                    setSideBarTab={setSideBarTab}
                    switchToDocument={(id: string) => {
                      const doc = documents.find((d) => d.id === id);
                      if (doc) {
                        setDocument(doc);
                      }
                    }}
                    loading={
                      contractsLoading ||
                      contractLoading ||
                      contractFetching ||
                      documentsLoading ||
                      documentsFetching ||
                      aiOutputsLoading ||
                      aiOutputsFetching
                    }
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={showSnackbar}
        onClose={handleClose}
        message={t("Analysis requested")}
        key={"bottomright"}
        autoHideDuration={5000}
        color="primary"
      />
    </React.Fragment>
  );
};

export default AIContractDetail;
